import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Global} from '@emotion/react'
import Helmet from 'react-helmet'
import {useChakraContext} from '@chakra-ui/react3'

/**
 * Name of the CSS custom property that carries the layout variant the page asks
 * for. Encapsulated here so pages (via `HeaderVariant`) never hardcode the raw
 * name.
 *
 * - `0` (or absent) → `inline`: the header reserves its vertical space and the
 *   page content starts below it. This is the default.
 * - `1` → `floating`: the header reserves no space and the page content passes
 *   behind it.
 *
 * Registered as an `@property` (number) in `app/leap/theme/global-css.js`.
 */
export const HEADER_FLOATING_VAR = '--header-floating'

/**
 * Name of the CSS custom property that carries the variant the breakpoint
 * grants: `HEADER_FLOATING_VAR` from the Leap `s` breakpoint up, `0` below it.
 * `app/leap/theme/global-css.js` owns that derivation. Lay out against this
 * one, never the requested one.
 */
export const HEADER_FLOATING_ACTIVE_VAR = '--header-floating-active'

const FLOATING_VALUE = {inline: 0, floating: 1}

const DEFAULT_VARIANT = 'inline'

// Subscribers are notified that the cascade moved, and nothing more. Carrying
// the variant itself would mean tracking it here, and module state could only
// ever be a guess: effects do not run during SSR, so it would still read
// `DEFAULT_VARIANT` on the server and through the first client render even for a
// page rendering `<HeaderVariant variant="floating">` — wrong exactly when a
// renderer would most want to trust it. The CSS cascade is correct from the very
// first paint and stays the only source of truth.
//
// It also keeps every notification unconditional. Suppressing the ones that look
// redundant would save a single animation-frame read — subscribers re-read the
// live cascade either way — at the price of an unwritten "only one
// `HeaderVariant` is ever mounted" invariant, since an unmount cannot know
// whether another one is still holding the cascade where it is.
const subscribers = new Set()

/**
 * Subscribes to changes of the published header layout variant.
 *
 * `<Global>` publishes the variant into the CSS cascade, which React cannot
 * observe: anything deriving state from the header height chain has no way to
 * learn that the chain changed. This carries that one bit — *it changed* —
 * alongside the CSS, so subscribers re-read the computed values at the moment
 * the new variant lands rather than at some earlier point they guessed at.
 *
 * Fires on every `HeaderVariant` mount, variant change and unmount — including
 * across the loading gap of a client-side navigation, where a page still
 * fetching its content renders no `HeaderVariant` at all, so the cascade falls
 * back to the registered initial value and the real variant is announced only
 * once that content has arrived. Notifications are not deduplicated, so a
 * subscriber may be called when nothing it reads has actually moved.
 *
 * @param {Function} onVariantChange - Called after the new variant is in the cascade.
 * @returns {Function} Unsubscribe.
 */
export const subscribeHeaderVariantChange = (onVariantChange) => {
    subscribers.add(onVariantChange)
    return () => subscribers.delete(onVariantChange)
}

const announceVariantChange = () => subscribers.forEach((onVariantChange) => onVariantChange())

/**
 * Name of the CSS custom property that carries the current page's background
 * color.
 *
 * Painted as the page canvas by the `html` rule in
 * `app/leap/theme/global-css.js`, which also registers it. Anything on that
 * canvas with no background of its own -- the header's card gutter, the
 * breadcrumb strip -- just shows the page through, so nothing has to read this
 * var to stay in sync.
 */
export const PAGE_BG_VAR = '--page-bg'

/**
 * Declares the navigation header layout variant, and the page's background
 * color, for the current page by publishing `--header-floating` / `--page-bg`
 * on `:root`.
 *
 * Rendered through Chakra/emotion's `<Global>`, which emits a real `<style>`
 * element during SSR, so both are correct on the very first paint — no
 * `useEffect`, no mounted-guard, no flash or hydration mismatch. Pages that
 * render no `HeaderVariant` default to `inline`: `--header-floating` is a
 * registered `@property` (`app/leap/theme/global-css.js`) with that initial
 * value, so every consumer reads it via a bare `var(...)`. `--page-bg` is left
 * unpublished instead of defaulted here — its own `@property` carries no
 * `initial-value`, so the `html` rule's fallback (the theme-aware `bg` token)
 * is what such a page paints.
 *
 * A change is also announced to `subscribeHeaderVariantChange` subscribers, for
 * those that derive state from the CSS rather than merely being styled by it.
 * That happens in an effect, after `<Global>` has applied the new cascade, so a
 * subscriber re-reading computed values on the notification reads the variant
 * being announced.
 *
 * @component
 * @param {'inline'|'floating'} [props.variant='inline'] - The header layout.
 * @param {string} [props.bg] - The page background: a token path
 *   (e.g. `background.section.subtle`) or an already-resolved `var(...)`.
 *   Omitted leaves `--page-bg` unpublished, so the page falls back to the `bg`
 *   token.
 * @param {'light'|'dark'} [props.theme] - Published as `data-leap-theme` on
 *   `<html>`, the scope the `background.section.*` tokens resolve light/dark
 *   against.
 * @returns {JSX.Element} A `<Global>` publishing the variant/bg vars on `:root`.
 */
export const HeaderVariant = ({variant = DEFAULT_VARIANT, bg, theme}) => {
    const system = useChakraContext()
    const resolvedVariant = Object.hasOwn(FLOATING_VALUE, variant) ? variant : DEFAULT_VARIANT
    // `--page-bg` is a custom property, not a property Panda's `css()` knows
    // to resolve color tokens for on its own -- routing `bg` through `color`
    // first is what actually turns a token path (e.g. `background.section.subtle`)
    // into its var(...); an already-resolved var(...) string matches no token
    // and passes through unchanged, so callers can hand either.
    const resolvedBg = bg && system.css({color: bg}).color

    useEffect(() => {
        announceVariantChange()
        // Unmounting removes the `<Global>` style, so the cascade moves again.
        return announceVariantChange
    }, [resolvedVariant])

    return (
        <>
            {theme && <Helmet htmlAttributes={{'data-leap-theme': theme}} />}
            <Global
                styles={{
                    ':root': system.css({
                        [HEADER_FLOATING_VAR]: FLOATING_VALUE[resolvedVariant],
                        ...(resolvedBg && {[PAGE_BG_VAR]: resolvedBg})
                    })
                }}
            />
        </>
    )
}

HeaderVariant.propTypes = {
    variant: PropTypes.oneOf(Object.keys(FLOATING_VALUE)),
    bg: PropTypes.string,
    theme: PropTypes.oneOf(['light', 'dark'])
}
